import { FormField, useRestApiProvider } from "@jugl-web/rest-api";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useCallback, useMemo } from "react";
import {
  convertSnakeCaseToReadableString,
  useAppVariant,
  useTranslations,
} from "@jugl-web/utils";
import { ReactComponent as TextFieldIcon } from "./assets/text-field.svg";
import { ReactComponent as DropdownFieldIcon } from "./assets/dropdown-field.svg";
import { ReactComponent as DateFieldIcon } from "./assets/date-field.svg";
import { ReactComponent as NumberFieldIcon } from "./assets/number-field.svg";
import { ReactComponent as URLFieldIcon } from "./assets/url-field.svg";
import { ReactComponent as MobileFieldIcon } from "./assets/mobile-field.svg";
import { ReactComponent as EmailFieldIcon } from "./assets/email-field.svg";
import { ReactComponent as LinkFieldIcon } from "./assets/link-field.svg";
import { ReactComponent as StatusFieldIcon } from "./assets/status-field.svg";

import { ReactComponent as CustomTextFieldIcon } from "./assets/custom-text-field.svg";
import { ReactComponent as CustomDateFieldIcon } from "./assets/custom-date-field.svg";
import { ReactComponent as CustomNumberFieldIcon } from "./assets/custom-number-field.svg";
import { ReactComponent as CustomDropdownFieldIcon } from "./assets/custom-dropdown-field.svg";
import { ReactComponent as CustomLinkFieldIcon } from "./assets/custom-link-field.svg";
import { ReactComponent as CustomMobileFieldIcon } from "./assets/custom-mobile-field.svg";
import { ReactComponent as CustomEmailFieldIcon } from "./assets/custom-email-field.svg";

import { ReactComponent as AllIcon } from "./assets/all.svg";

const uneditableFields = new Set(["last_order_placed", "total_purchased"]);
const excludedFieldsFromSearchQueryFilters = new Set([
  "last_order_placed",
  "total_purchased",
]);

const defaultFieldTypeIcons = {
  text: <TextFieldIcon />,
  date: <DateFieldIcon />,
  number: <NumberFieldIcon />,
  dropdown: <DropdownFieldIcon />,
  url: <LinkFieldIcon />,
  status: <StatusFieldIcon />,
  mobile: <MobileFieldIcon />,
  email: <EmailFieldIcon />,
};

const customFieldTypeIcons = {
  text: <CustomTextFieldIcon />,
  date: <CustomDateFieldIcon />,
  number: <CustomNumberFieldIcon />,
  dropdown: <CustomDropdownFieldIcon />,
  url: <CustomLinkFieldIcon />,
  mobile: <CustomMobileFieldIcon />,
  email: <CustomEmailFieldIcon />,
};

export const useCustomerFields = ({
  entityId,
  preloadedFieldsMap,
}: {
  entityId: string | undefined;
  preloadedFieldsMap?: Record<string, FormField>;
}) => {
  const { t } = useTranslations();
  const { customersFormFieldApi } = useRestApiProvider();
  const { isCx } = useAppVariant();
  const { data: formFields, isLoading } =
    customersFormFieldApi.useFormFieldsQuery(
      entityId && !preloadedFieldsMap ? { entityId } : skipToken
    );

  const filteredFormFields = useMemo(() => {
    const fields = formFields?.data || [];

    if (!isCx) return fields;

    return fields.filter(
      (field) =>
        field.name !== "last_order_placed" &&
        field.name !== "total_purchased" &&
        field.name !== "status" &&
        field.name !== "referred_by"
    );
  }, [formFields?.data, isCx]);

  const fieldsMap = useMemo(() => {
    if (preloadedFieldsMap) {
      return preloadedFieldsMap;
    }
    return filteredFormFields.reduce(
      (acc: Record<string, FormField>, field) => {
        acc[field.name] = field;
        return acc;
      },
      {}
    );
  }, [filteredFormFields, preloadedFieldsMap]);

  const getFieldDefinition = useCallback(
    (fieldId: string) => {
      const field = filteredFormFields.find((item) => item.id === fieldId);
      return field || undefined;
    },
    [filteredFormFields]
  );
  const editableFormFields = useMemo(() => {
    return filteredFormFields.filter(
      (field) => !uneditableFields.has(field.name) && !field.read_only
    );
  }, [filteredFormFields]);

  const fieldTypeLabelAndIcon = {
    text: {
      name: t({
        id: "form-controls.field-type.text",
        defaultMessage: "Text",
      }),
      icon: <TextFieldIcon />,
    },
    dropdown: {
      name: t({
        id: "form-controls.field-type.dropdown",
        defaultMessage: "Dropdown",
      }),
      icon: <DropdownFieldIcon />,
    },
    date: {
      name: t({
        id: "form-controls.field-type.date",
        defaultMessage: "Date",
      }),
      icon: <DateFieldIcon />,
    },
    number: {
      name: t({
        id: "form-controls.field-type.numeric",
        defaultMessage: "Numeric",
      }),
      icon: <NumberFieldIcon />,
    },
    url: {
      name: t({
        id: "form-controls.field-type.link",
        defaultMessage: "Link",
      }),
      icon: <URLFieldIcon />,
    },
    mobile: {
      name: t({
        id: "form-controls.field-type.phone-number",
        defaultMessage: "Phone number",
      }),
      icon: <MobileFieldIcon />,
    },
    email: {
      name: t({
        id: "form-controls.field-type.email",
        defaultMessage: "Email",
      }),
      icon: <EmailFieldIcon />,
    },
  };

  const searchQueryFilters = useMemo(() => {
    return [
      {
        id: "all",
        title: t({
          id: "tasks-page.all-columns",
          defaultMessage: "All columns",
        }),
        backgroundColor: "bg-[#6192BE]",
        icon: <AllIcon />,
        isDefault: false,
      },
      ...filteredFormFields
        .filter(
          (field) => !excludedFieldsFromSearchQueryFilters.has(field.name)
        )
        .map((field) => {
          return {
            id: field.id,
            name: field.name,
            title: convertSnakeCaseToReadableString(field.name),
            icon: field.is_cust_field
              ? customFieldTypeIcons[
                  field.type as keyof typeof customFieldTypeIcons
                ]
              : defaultFieldTypeIcons[
                  field.type as keyof typeof defaultFieldTypeIcons
                ],
            backgroundColor: "bg-grey-200",
            isDefault: field.name === "first_name",
          };
        }),
    ];
  }, [filteredFormFields, t]);

  return {
    formFields: filteredFormFields,
    isLoading,
    editableFormFields,
    getFieldDefinition,
    fieldsMap,
    fieldTypeLabelAndIcon,
    defaultFieldTypeIcons,
    customFieldTypeIcons,
    searchQueryFilters,
  };
};
